import clsx from 'clsx'
import merge from 'deepmerge'
import { Link } from 'expo-router'
import type { LinkProps } from 'expo-router/build/link/Link'
import {
  Children,
  createContext,
  useContext,
  useId,
  useState,
  type ComponentProps,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import { Pressable, View, type LayoutChangeEvent } from 'react-native'
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import type { ViewProps } from 'react-native-svg/lib/typescript/fabric/utils'

import type { TCardProps } from '@/src/elements/card'
import { AudioCard, type TAudioCardProps } from '@/src/elements/card/audio/AudioCard'
import { YoutubeCard, type TYoutubeCardProps } from '@/src/elements/card/video'
import { Iframe as ContentIframe } from '@/src/elements/iframe'
import { Image, type TImageProps } from '@/src/elements/image'
import { Icon } from '@/src/elements/media/Icon'
import { Typography, type TTypographyProps } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { assignAnimatedValue } from '@/src/general/utils/assignAnimatedValue'

import type { TContentTextValue, TContentType, TContentValueToProps } from '../content.types'
import { ArticleSubtitle, ImportantSection, Paragraph, QuoteSection } from './components'

const bulletSymbol = '\u2022'

/*******************
 * Inline elements *
 *******************/

export const ContentNl = (): ReactNode => '\n'

export const ContentText = ({
  children,
  className,
  format,
  ...props
}: Omit<TTypographyProps, 'children'> & TContentValueToProps<TContentTextValue> & PropsWithChildren): ReactNode =>
  format ? (
    <Typography
      colors="secondary"
      contrast="high"
      ariaRole="span"
      className={clsx(
        'inline',
        format.includes('bold') && 'font-bold',
        format.includes('italic') && 'italic',
        'leading-7',
        'whitespace-normal',
        className,
      )}
      {...props}
    >
      {children}
    </Typography>
  ) : (
    children
  )

export const ContentLink = ({
  children,
  format,
  className,
  ...otherProps
}: Omit<LinkProps, 'children'> & TContentValueToProps<TContentTextValue> & PropsWithChildren): ReactNode => (
  <Link {...otherProps} className={className}>
    {children || (
      <ContentText className={className} format={format}>
        {children}
      </ContentText>
    )}
  </Link>
)

export const ContentSummary = ({ children, className, ...props }: ViewProps): ReactNode => (
  <Typography colors="primary" contrast="default" black size="xl" {...props}>
    {children}
  </Typography>
)

/***********************
 * Text block elements *
 ***********************/

export const ContentP = (props: TTypographyProps): ReactNode => <Paragraph {...props} />

export const ContentHeading = (props: TTypographyProps): ReactNode => <ArticleSubtitle {...props} />

/***************************
 * Text container elements *
 ***************************/

export const ContentImportant = ({ children, className, ...props }: TCardProps): ReactNode => (
  <View className={clsx('pb-4 pt-2', className)}>
    <ContentContextUpdater
      props={{
        p: { className: 'first:pt-0 last:pb-0 font-bold text-primary-text-contrast-default' },
        text: { format: 'bold', className: 'text-primary-text-contrast-default' },
        link: { format: 'bold', className: 'text-content-link' },
      }}
    >
      <ImportantSection {...props}>{children}</ImportantSection>
    </ContentContextUpdater>
  </View>
)

export const ContentBlockquote = ({ children, className, ...props }: ViewProps): ReactNode => (
  <View className={clsx('pl-2', className)} {...props}>
    {children}
  </View>
)

const DETAILS_TIMING_CONFIG = { duration: 300, easing: Easing.inOut(Easing.ease) }
// Bottom padding of the animated container, kept visible below the white card during the animation
const DETAILS_BOTTOM_PADDING = 8

export const ContentDetails = ({
  children,
  className,
  summary,
  open = false,
  ...props
}: PropsWithChildren<{ summary?: ReactNode; open?: boolean }> & ViewProps): ReactNode => {
  const [isOpen, setIsOpen] = useState(open)
  const progress = useSharedValue(open ? 1 : 0)
  const contentHeight = useSharedValue(0)

  const toggle = () => {
    assignAnimatedValue(progress, withTiming(isOpen ? 0 : 1, DETAILS_TIMING_CONFIG))
    setIsOpen(!isOpen)
  }

  const handleContentLayout = ({ nativeEvent: { layout } }: LayoutChangeEvent) => {
    assignAnimatedValue(contentHeight, layout.height)
  }

  const animatedContainerStyle = useAnimatedStyle(() => {
    const height = (contentHeight.get() + DETAILS_BOTTOM_PADDING) * progress.get()
    // The padding would otherwise keep the container open when its height reaches 0
    return { height, paddingBottom: Math.min(height, DETAILS_BOTTOM_PADDING) }
  })

  const animatedChevronStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${progress.get() * 90}deg` }],
  }))

  return (
    <View
      className={clsx(
        'bg-primary-bg-medium mb-4 overflow-hidden rounded-none w-screen -left-5 md:rounded-2xl relative md:w-full md:left-0',
        className,
      )}
      {...props}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: isOpen }}
        onPress={toggle}
        className="flex-row items-center gap-2 p-5"
      >
        <Animated.View testID={TEST_ID.CONTENT_DETAILS.CHEVRON} style={animatedChevronStyle}>
          <Icon size="md" name={ICON.CHEVRON_RIGHT} className="text-primary-text-contrast-default" />
        </Animated.View>
        {summary}
      </Pressable>
      <Animated.View
        testID={TEST_ID.CONTENT_DETAILS.COLLAPSIBLE}
        style={animatedContainerStyle}
        className="overflow-hidden px-2"
        aria-hidden={!isOpen}
      >
        <View className="flex-1 overflow-hidden rounded-lg">
          <View
            testID={TEST_ID.CONTENT_DETAILS.CONTENT}
            onLayout={handleContentLayout}
            className="absolute top-0 left-0 right-0 p-4 bg-white rounded-lg"
          >
            {children}
          </View>
        </View>
      </Animated.View>
    </View>
  )
}

export const ContentQuote = ({ children, className, ...props }: ViewProps): ReactNode => (
  <View className={clsx('pb-4 pt-2', className)}>
    <ContentContextUpdater props={{ p: { className: 'italic first:pt-0 last:pb-0' } }}>
      <QuoteSection {...props}>
        <Paragraph className="block gap-2 py-0 pt-0 italic">{children}</Paragraph>
      </QuoteSection>
    </ContentContextUpdater>
  </View>
)

export const ContentList = ({
  children,
  listType,
}: PropsWithChildren<{ listType: 'number' | 'bullet' }>): ReactNode => {
  const id = useId()
  return (
    <View className="gap-2">
      {Children.map(children, (child, index) => (
        <ContentContextUpdater
          // oxlint-disable-next-line react/no-array-index-key
          key={`list-${id}-${index}`}
          props={{
            li: {
              listMarker: listType === 'number' ? `${(index + 1).toString()}.` : bulletSymbol,
              className: 'text-mdlc text-secondary-text-contrast-high',
            },
            text: { className: 'text-mdlc text-secondary-text-contrast-high w-fit' },
          }}
        >
          {child}
        </ContentContextUpdater>
      ))}
    </View>
  )
}

export const ContentLi = ({
  children,
  className,
  listMarker = bulletSymbol,
}: { listMarker?: string } & ViewProps): ReactNode => (
  <View className="flex-row items-baseline">
    <ContentText className={clsx(className, 'px-2')} format="normal">
      {listMarker}
    </ContentText>
    {/* ContentText instead of a mere View because the content can be raw text nodes */}
    <ContentText className="w-fit flex-1 flex-col" format="normal">
      {children}
    </ContentText>
  </View>
)

/******************
 * Media elements *
 ******************/

export const ContentAudio = ({ className, ...props }: TAudioCardProps): ReactNode => {
  return (
    <View className={clsx('w-full pb-4 pt-2', className)}>
      <AudioCard {...props} />
    </View>
  )
}

export const ContentImg = ({
  width: propWidth,
  height: propHeight,
  className,
  ...props
}: TImageProps & { width?: number; height?: number }): ReactNode => {
  const [width, setWidth] = useState(propWidth)
  const [height, setHeight] = useState(propHeight)

  return (
    <View className={clsx('flex w-full items-center pb-4 pt-2', className)}>
      <View style={{ width, aspectRatio: width && height ? width / height : 16 / 9 }} className="max-w-full">
        <Image
          onLoad={({ source }) => {
            if (!width) {
              setWidth(source.width)
            }
            if (!height) {
              setHeight(source.height)
            }
          }}
          {...props}
        />
      </View>
    </View>
  )
}

export const ContentYoutube = ({ className, ...props }: TYoutubeCardProps): ReactNode => (
  <View className={clsx('w-full pb-4 pt-2', className)}>
    <YoutubeCard {...props} />
  </View>
)

/***********
 * Context *
 ***********/

const defaultContext = {
  Components: {
    nl: ContentNl,
    text: ContentText,
    link: ContentLink,
    p: ContentP,
    heading: ContentHeading,
    important: ContentImportant,
    img: ContentImg,
    summary: ContentSummary,
    quote: ContentQuote,
    blockquote: ContentBlockquote,
    details: ContentDetails,
    audio: ContentAudio,
    youtube: ContentYoutube,
    list: ContentList,
    li: ContentLi,
    iframe: ContentIframe,
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  } satisfies Record<TContentType, (props: any) => ReactNode>,
}

type TContentComponentProps<Type extends TContentType> = ComponentProps<(typeof defaultContext.Components)[Type]>

export type TContentContext = {
  props?: {
    [Type in TContentType]?: TContentComponentProps<Type>
  }
} & Pick<typeof defaultContext, 'Components'>

export const ContentContext = createContext<TContentContext>(defaultContext)

export type TContentContextProviderProps = TDeepPartial<TContentContext>

export const ContentContextProvider = ({
  children,
  ...customizations
}: PropsWithChildren<TContentContextProviderProps>): ReactNode => (
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- deepmerge yields complete context
  <ContentContext.Provider value={merge(defaultContext, customizations) as TContentContext}>
    {children}
  </ContentContext.Provider>
)

export const ContentContextUpdater = ({
  children,
  ...updates
}: PropsWithChildren<TContentContextProviderProps>): ReactNode => {
  const currentContext = useContext(ContentContext)

  // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  return <ContentContext.Provider value={merge(currentContext, updates)}>{children}</ContentContext.Provider>
}
